'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';

export const BOOKING_PARAMS = {
  DATE: 'date',
  DATE_FROM: 'dateFrom',
  DATE_TO: 'dateTo',
  ACTIVITY_ID: 'activityId',
  MENTEE: 'mentee'
} as const;

interface ActivityOption {
  value: string;
  label: string;
}

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface FilterOption {
  id: string;
  type: 'dateRange' | 'select';
  label: string;
  value?: string;
  options?: ActivityOption[];
}

export const useBookingFilters = (_activities: ActivityOption[] = [], _isProfessional: boolean = false) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Get current filters from URL
  const getCurrentFilters = useCallback(() => {
    const date = searchParams.get(BOOKING_PARAMS.DATE);
    const dateFrom = searchParams.get(BOOKING_PARAMS.DATE_FROM);
    const dateTo = searchParams.get(BOOKING_PARAMS.DATE_TO);
    const activityId = searchParams.get(BOOKING_PARAMS.ACTIVITY_ID);
    const mentee = searchParams.get(BOOKING_PARAMS.MENTEE);

    return {
      date: date || undefined,
      startDate: dateFrom || undefined,
      endDate: dateTo || undefined,
      activityId: activityId || undefined,
      menteeClientId: mentee || undefined
    };
  }, [searchParams]);

  // Handle filter changes
  const handleFilterChange = useCallback(
    (paramName: string, value: any) => {
      const params = new URLSearchParams(searchParams.toString());

      if (paramName === BOOKING_PARAMS.DATE_FROM && value?.from && value?.to) {
        // Handle date range - use local date format to avoid timezone shifts
        const fromDate = new Date(value.from.getTime() - value.from.getTimezoneOffset() * 60000);
        const toDate = new Date(value.to.getTime() - value.to.getTimezoneOffset() * 60000);
        params.set(BOOKING_PARAMS.DATE_FROM, fromDate.toISOString().split('T')[0]);
        params.set(BOOKING_PARAMS.DATE_TO, toDate.toISOString().split('T')[0]);
      } else if (paramName === BOOKING_PARAMS.DATE_FROM && (!value?.from || !value?.to)) {
        // Reset date range
        params.delete(BOOKING_PARAMS.DATE_FROM);
        params.delete(BOOKING_PARAMS.DATE_TO);
      } else if (value) {
        params.set(paramName, value);
      } else {
        params.delete(paramName);
      }

      router.push(`?${params.toString()}`);
    },
    [router, searchParams]
  );

  // Get date range from URL params
  const getDateRange = useCallback((): DateRange => {
    const dateFrom = searchParams.get(BOOKING_PARAMS.DATE_FROM);
    const dateTo = searchParams.get(BOOKING_PARAMS.DATE_TO);

    return {
      from: dateFrom ? new Date(dateFrom) : null,
      to: dateTo ? new Date(dateTo) : null
    };
  }, [searchParams]);

  // Build filter options
  const filters = useMemo((): FilterOption[] => {
    const filtersList: FilterOption[] = [];

    // Date range filter (always present)
    filtersList.push({
      id: 'dateRange',
      type: 'dateRange',
      label: 'Date Range'
    });

    return filtersList;
  }, []);

  return {
    filters,
    getCurrentFilters,
    handleFilterChange,
    getDateRange
  };
};
